/*
 * MinesCore — the rules of minesweeper and nothing else.
 *
 *   MinesCore.LEVELS / LIMITS      the three classic boards, the bounds of a custom one
 *   MinesCore.topology(w, h)       neighbour lists of a board, cached
 *   MinesCore.place(w, h, n, rng, first)   a random layout that keeps the first cell an opening
 *   MinesCore.numbers(w, h, mine)  the digits
 *   MinesCore.threeBV(w, h, mine)  the least number of left clicks that solves the board
 *   MinesCore.Game                 one game: open / flag / chord / win / loss / take the loss back
 *   MinesCore.encodeBoard / decodeBoard   a board as a link: the mines themselves, not a seed
 *
 * A classic script without DOM: the page loads it with a tag, the worker with
 * importScripts, node tests through vm. Random numbers come only from the
 * generator passed in (GameKit.rng) — a board must be the same on every device.
 * Why each rule is shaped the way it is — docs/why/minesweeper.md.
 */
(function (root) {
    'use strict';

    var LEVELS = {
        beginner:     { w: 9,  h: 9,  mines: 10 },
        intermediate: { w: 16, h: 16, mines: 40 },
        expert:       { w: 30, h: 16, mines: 99 }
    };
    // A custom board: wide enough to be a game, small enough for a link and a screen.
    var LIMITS = { minW: 5, maxW: 50, minH: 5, maxH: 40, minMines: 1 };

    var HIDDEN = 0, OPEN = 1, FLAG = 2, QUESTION = 3;

    function maxMines(w, h) {
        // Room for an opening under the first click, and never a board of nothing but mines.
        return Math.max(1, Math.min((w - 1) * (h - 1), w * h - 9));
    }
    function clampBoard(w, h, mines) {
        w = Math.max(LIMITS.minW, Math.min(LIMITS.maxW, Math.round(+w) || LIMITS.minW));
        h = Math.max(LIMITS.minH, Math.min(LIMITS.maxH, Math.round(+h) || LIMITS.minH));
        mines = Math.max(LIMITS.minMines, Math.min(maxMines(w, h), Math.round(+mines) || LIMITS.minMines));
        return { w: w, h: h, mines: mines };
    }
    function levelOf(w, h, mines) {
        for (var id in LEVELS) {
            var L = LEVELS[id];
            if (L.w === w && L.h === h && L.mines === mines) return id;
        }
        return 'custom';
    }

    // ── Board geometry ───────────────────────────────────────────────────────
    var topoCache = {};
    function topology(w, h) {
        var key = w + 'x' + h, t = topoCache[key];
        if (t) return t;
        var n = w * h, nb = new Array(n);
        for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
                var list = [];
                for (var dy = -1; dy <= 1; dy++) {
                    for (var dx = -1; dx <= 1; dx++) {
                        if (!dx && !dy) continue;
                        var xx = x + dx, yy = y + dy;
                        if (xx >= 0 && xx < w && yy >= 0 && yy < h) list.push(yy * w + xx);
                    }
                }
                nb[y * w + x] = list;
            }
        }
        t = topoCache[key] = { w: w, h: h, n: n, nb: nb };
        return t;
    }

    function numbers(w, h, mine) {
        var t = topology(w, h), num = new Uint8Array(t.n);
        for (var i = 0; i < t.n; i++) {
            if (!mine[i]) continue;
            var l = t.nb[i];
            for (var k = 0; k < l.length; k++) num[l[k]]++;
        }
        return num;
    }

    function countMines(mine) {
        var c = 0;
        for (var i = 0; i < mine.length; i++) if (mine[i]) c++;
        return c;
    }

    // 3BV: every opening (a connected patch of blank cells with its rim of
    // numbers) costs one click, every number that touches no blank costs one.
    function threeBV(w, h, mine, num) {
        var t = topology(w, h), n = t.n, nb = t.nb;
        num = num || numbers(w, h, mine);
        var seen = new Uint8Array(n), bv = 0, stack = [];
        for (var i = 0; i < n; i++) {
            if (mine[i] || num[i] !== 0 || seen[i]) continue;
            bv++;
            seen[i] = 1;
            stack.push(i);
            while (stack.length) {
                var z = stack.pop(), l = nb[z];
                for (var k = 0; k < l.length; k++) {
                    var c = l[k];
                    if (seen[c]) continue;
                    seen[c] = 1;
                    if (num[c] === 0) stack.push(c);
                }
            }
        }
        for (var j = 0; j < n; j++) if (!mine[j] && !seen[j]) bv++;
        return bv;
    }

    // A random layout. The first cell and its neighbours stay clear, so the
    // first click opens an area; a board too dense for that keeps at least the
    // cell itself clear.
    function place(w, h, count, rng, first) {
        var t = topology(w, h), n = t.n, blocked = new Uint8Array(n), i;
        if (first != null && first >= 0 && first < n) {
            var zone = [first].concat(t.nb[first]);
            if (n - zone.length >= count) {
                for (i = 0; i < zone.length; i++) blocked[zone[i]] = 1;
            } else if (n - 1 >= count) {
                blocked[first] = 1;
            }
        }
        var cells = [];
        for (i = 0; i < n; i++) if (!blocked[i]) cells.push(i);
        count = Math.min(count, cells.length);
        var mine = new Uint8Array(n);
        for (var k = 0; k < count; k++) {
            var j = k + rng.int(cells.length - k), tmp = cells[k];
            cells[k] = cells[j];
            cells[j] = tmp;
            mine[cells[k]] = 1;
        }
        return mine;
    }

    // ── Compact arrays ───────────────────────────────────────────────────────
    function packBits(arr) {
        var out = new Uint8Array(Math.ceil(arr.length / 8));
        for (var i = 0; i < arr.length; i++) if (arr[i]) out[i >> 3] |= 1 << (i & 7);
        return out;
    }
    function unpackBits(bytes, n) {
        var out = new Uint8Array(n);
        for (var i = 0; i < n; i++) out[i] = (bytes[i >> 3] >> (i & 7)) & 1;
        return out;
    }
    function pack2(arr) {
        var out = new Uint8Array(Math.ceil(arr.length / 4));
        for (var i = 0; i < arr.length; i++) out[i >> 2] |= (arr[i] & 3) << ((i & 3) * 2);
        return out;
    }
    function unpack2(bytes, n) {
        var out = new Uint8Array(n);
        for (var i = 0; i < n; i++) out[i] = (bytes[i >> 2] >> ((i & 3) * 2)) & 3;
        return out;
    }
    function b64() {
        var kit = root.GameKit;
        if (!kit || !kit.share) throw new Error('MinesCore needs GameKit.share');
        return kit.share;
    }

    // ── A board as a link ────────────────────────────────────────────────────
    // The mines themselves, not the seed that made them: a link sent today
    // must open the same board after the generator has been improved.
    //   byte 0  format (1)      byte 3  start column
    //   byte 1  width           byte 4  start row
    //   byte 2  height          byte 5  bit 0 — a no-guess board
    //   then the mines, one bit per cell, row by row
    function encodeBoard(b) {
        var bits = packBits(b.mine), out = new Uint8Array(6 + bits.length);
        out[0] = 1;
        out[1] = b.w;
        out[2] = b.h;
        out[3] = b.start % b.w;
        out[4] = Math.floor(b.start / b.w);
        out[5] = b.noGuess ? 1 : 0;
        out.set(bits, 6);
        return b64().toBase64Url(out);
    }
    function decodeBoard(text) {
        var bytes;
        try { bytes = b64().fromBase64Url(text); } catch (_) { return null; }
        if (!bytes || bytes.length < 7 || bytes[0] !== 1) return null;
        var w = bytes[1], h = bytes[2], sx = bytes[3], sy = bytes[4];
        if (w < LIMITS.minW || w > LIMITS.maxW || h < LIMITS.minH || h > LIMITS.maxH) return null;
        if (sx >= w || sy >= h) return null;
        var n = w * h;
        if (bytes.length !== 6 + Math.ceil(n / 8)) return null;
        var mine = unpackBits(bytes.subarray(6), n), count = countMines(mine), start = sy * w + sx;
        if (count < 1 || count > n - 1 || mine[start]) return null;
        // Bits past the last cell must be zero: a link is not a place for stray data.
        for (var i = n; i < (bytes.length - 6) * 8; i++) if ((bytes[6 + (i >> 3)] >> (i & 7)) & 1) return null;
        return { w: w, h: h, mine: mine, count: count, start: start, noGuess: !!(bytes[5] & 1) };
    }

    // ── One game ─────────────────────────────────────────────────────────────
    //   new Game({w, h, mines, questions})
    //   setMines(mine)        the layout arrives with the first click
    //   open(i) / toggleFlag(i) / chord(i)   → {changed: [cells], boom: [cells] | null, won, noop}
    //   undoLoss()            practice aid: the fatal move never happened; the game is `assisted`
    //   snapshot() / Game.restore(obj)
    function Game(o) {
        o = o || {};
        // The bounds of a custom board are the page's business (clampBoard):
        // the rules themselves hold on any rectangle.
        this.w = Math.max(1, o.w | 0);
        this.h = Math.max(1, o.h | 0);
        this.n = this.w * this.h;
        this.count = Math.max(0, Math.min(this.n, o.mines | 0));
        this.nb = topology(this.w, this.h).nb;
        this.mine = null;
        this.num = null;
        this.st = new Uint8Array(this.n);
        this.status = 'ready';
        this.opened = 0;
        this.flags = 0;
        this.bv = 0;
        this.clicks = { open: 0, flag: 0, chord: 0, wasted: 0 };
        this.questions = !!o.questions;
        this.assisted = false;
        this.hints = 0;
        this.undos = 0;
        this.boom = null;
        this.fatal = null;
    }

    Game.prototype.placed = function () { return !!this.mine; };
    Game.prototype.over = function () { return this.status === 'won' || this.status === 'lost'; };
    Game.prototype.minesLeft = function () { return this.count - this.flags; };
    Game.prototype.totalClicks = function () {
        var c = this.clicks;
        return c.open + c.flag + c.chord + c.wasted;
    };

    Game.prototype.setMines = function (mine) {
        if (!mine || mine.length !== this.n) throw new Error('MinesCore: layout does not fit the board');
        this.mine = mine;
        this.count = countMines(mine);
        this.num = numbers(this.w, this.h, mine);
        this.bv = threeBV(this.w, this.h, mine, this.num);
        return this;
    };

    Game.prototype._flood = function (start, changed) {
        var st = this.st, num = this.num, nb = this.nb, stack = [start];
        st[start] = OPEN;
        this.opened++;
        changed.push(start);
        while (stack.length) {
            var z = stack.pop();
            if (num[z] !== 0) continue;
            var l = nb[z];
            for (var k = 0; k < l.length; k++) {
                var c = l[k];
                // A blank area takes its question marks along; a flag stays —
                // it is the player's statement, even a wrong one.
                if (st[c] === OPEN || st[c] === FLAG) continue;
                st[c] = OPEN;
                this.opened++;
                changed.push(c);
                if (num[c] === 0) stack.push(c);
            }
        }
    };

    Game.prototype._lose = function (cells, opened, type) {
        this.status = 'lost';
        this.boom = cells.slice();
        this.fatal = { type: type, boom: cells.slice(), opened: opened.slice() };
        for (var i = 0; i < cells.length; i++) this.st[cells[i]] = OPEN;
    };

    Game.prototype._checkWin = function () {
        if (this.status !== 'playing' || this.opened !== this.n - this.count) return false;
        this.status = 'won';
        // The remaining mines are known now: show them flagged, as every
        // minesweeper since 1990 does.
        for (var i = 0; i < this.n; i++) if (this.mine[i] && this.st[i] !== FLAG) this.st[i] = FLAG;
        this.flags = this.count;
        return true;
    };

    Game.prototype.open = function (i) {
        var out = { changed: [], boom: null, won: false, noop: false };
        if (this.over() || !this.mine || i < 0 || i >= this.n) { out.noop = true; return out; }
        if (this.st[i] === OPEN || this.st[i] === FLAG) { this.clicks.wasted++; out.noop = true; return out; }
        this.status = 'playing';
        this.clicks.open++;
        if (this.mine[i]) {
            this._lose([i], [], 'open');
            out.changed.push(i);
            out.boom = [i];
            return out;
        }
        this._flood(i, out.changed);
        out.won = this._checkWin();
        return out;
    };

    Game.prototype.toggleFlag = function (i) {
        var out = { changed: [], boom: null, won: false, noop: false };
        if (this.over() || i < 0 || i >= this.n) { out.noop = true; return out; }
        var s = this.st[i];
        if (s === OPEN) { this.clicks.wasted++; out.noop = true; return out; }
        this.clicks.flag++;
        if (s === HIDDEN) { this.st[i] = FLAG; this.flags++; }
        else if (s === FLAG) { this.st[i] = this.questions ? QUESTION : HIDDEN; this.flags--; }
        else this.st[i] = HIDDEN;
        out.changed.push(i);
        return out;
    };

    Game.prototype.flagsAround = function (i) {
        var l = this.nb[i], f = 0;
        for (var k = 0; k < l.length; k++) if (this.st[l[k]] === FLAG) f++;
        return f;
    };
    Game.prototype.hiddenAround = function (i) {
        var l = this.nb[i], out = [];
        for (var k = 0; k < l.length; k++) if (this.st[l[k]] === HIDDEN || this.st[l[k]] === QUESTION) out.push(l[k]);
        return out;
    };

    // A chord: on an open digit that already has that many flags around it,
    // open everything else around. A wrong flag makes it fatal — that is the
    // rule, not a bug: the chord trusts the player's flags.
    Game.prototype.chord = function (i) {
        var out = { changed: [], boom: null, won: false, noop: false };
        if (this.over() || !this.mine || i < 0 || i >= this.n) { out.noop = true; return out; }
        if (this.st[i] !== OPEN || this.num[i] === 0 || this.flagsAround(i) !== this.num[i]) {
            this.clicks.wasted++;
            out.noop = true;
            return out;
        }
        var targets = this.hiddenAround(i);
        if (!targets.length) { this.clicks.wasted++; out.noop = true; return out; }
        this.clicks.chord++;
        var boom = [], k;
        for (k = 0; k < targets.length; k++) if (this.mine[targets[k]]) boom.push(targets[k]);
        for (k = 0; k < targets.length; k++) {
            var c = targets[k];
            if (this.mine[c] || this.st[c] === OPEN) continue;
            this._flood(c, out.changed);
        }
        if (boom.length) {
            this._lose(boom, out.changed, 'chord');
            for (k = 0; k < boom.length; k++) out.changed.push(boom[k]);
            out.boom = boom;
            return out;
        }
        out.won = this._checkWin();
        return out;
    };

    // Practice aid after a loss: the fatal move is taken back, the mines it
    // hit are flagged, and the game goes on — marked as assisted, so it sets
    // no record.
    Game.prototype.undoLoss = function () {
        var out = { changed: [], boom: null, won: false, noop: false };
        if (this.status !== 'lost' || !this.fatal) { out.noop = true; return out; }
        var f = this.fatal, k;
        for (k = 0; k < f.opened.length; k++) {
            this.st[f.opened[k]] = HIDDEN;
            this.opened--;
            out.changed.push(f.opened[k]);
        }
        for (k = 0; k < f.boom.length; k++) {
            this.st[f.boom[k]] = FLAG;
            this.flags++;
            out.changed.push(f.boom[k]);
        }
        if (f.type === 'chord') {
            // The chord was fatal because a flag was wrong: take the wrong ones off.
            for (k = 0; k < this.n; k++) {
                if (this.st[k] === FLAG && !this.mine[k]) { this.st[k] = HIDDEN; this.flags--; out.changed.push(k); }
            }
        }
        this.status = 'playing';
        this.boom = null;
        this.fatal = null;
        this.assisted = true;
        this.undos++;
        return out;
    };

    // What the player can see: the solver is given this and never the mines.
    Game.prototype.view = function () {
        var n = this.n, open = new Uint8Array(n), num = new Uint8Array(n), flag = new Uint8Array(n);
        for (var i = 0; i < n; i++) {
            if (this.st[i] === OPEN && !(this.mine && this.mine[i])) { open[i] = 1; num[i] = this.num[i]; }
            else if (this.st[i] === FLAG) flag[i] = 1;
        }
        return { w: this.w, h: this.h, mines: this.count, open: open, num: num, flag: flag };
    };

    // How much of the board's 3BV is done — the honest progress of a lost game.
    Game.prototype.solvedBV = function () {
        if (!this.mine) return 0;
        var n = this.n, nb = this.nb, num = this.num, st = this.st, mine = this.mine;
        var seen = new Uint8Array(n), done = 0, stack = [];
        for (var i = 0; i < n; i++) {
            if (mine[i] || num[i] !== 0 || seen[i]) continue;
            var opened = st[i] === OPEN;
            seen[i] = 1;
            stack.push(i);
            while (stack.length) {
                var z = stack.pop(), l = nb[z];
                for (var k = 0; k < l.length; k++) {
                    var c = l[k];
                    if (seen[c]) continue;
                    seen[c] = 1;
                    if (num[c] === 0) stack.push(c);
                }
            }
            if (opened) done++;
        }
        for (var j = 0; j < n; j++) if (!mine[j] && !seen[j] && st[j] === OPEN) done++;
        return done;
    };

    Game.prototype.snapshot = function () {
        var s = b64(), c = this.clicks;
        return {
            v: 1, w: this.w, h: this.h, c: this.count, s: this.status,
            m: this.mine ? s.toBase64Url(packBits(this.mine)) : null,
            st: s.toBase64Url(pack2(this.st)),
            ck: [c.open, c.flag, c.chord, c.wasted],
            q: this.questions ? 1 : 0, a: this.assisted ? 1 : 0, hn: this.hints, un: this.undos,
            bm: this.boom, ft: this.fatal
        };
    };

    // A saved game comes back from storage: anything that does not add up is
    // null, never a half-restored board.
    Game.restore = function (o) {
        try {
            if (!o || o.v !== 1) return null;
            if (!(o.w >= 1 && o.w <= LIMITS.maxW && o.h >= 1 && o.h <= LIMITS.maxH) || o.w % 1 || o.h % 1) return null;
            if (!(o.c >= 0 && o.c <= o.w * o.h)) return null;
            var g = new Game({ w: o.w, h: o.h, mines: o.c, questions: !!o.q });
            var s = b64(), n = g.n;
            if (o.m) {
                var mb = s.fromBase64Url(o.m);
                if (!mb || mb.length !== Math.ceil(n / 8)) return null;
                g.setMines(unpackBits(mb, n));
            }
            var sb = s.fromBase64Url(o.st);
            if (!sb || sb.length !== Math.ceil(n / 4)) return null;
            g.st = unpack2(sb, n);
            for (var i = 0; i < n; i++) {
                if (g.st[i] === OPEN) { if (!g.mine) return null; if (!g.mine[i]) g.opened++; }
                else if (g.st[i] === FLAG) g.flags++;
            }
            if (['ready', 'playing', 'won', 'lost'].indexOf(o.s) < 0) return null;
            if (o.s !== 'ready' && !g.mine) return null;
            g.status = o.s;
            var ck = o.ck || [];
            g.clicks = { open: ck[0] | 0, flag: ck[1] | 0, chord: ck[2] | 0, wasted: ck[3] | 0 };
            g.assisted = !!o.a;
            g.hints = o.hn | 0;
            g.undos = o.un | 0;
            g.boom = Array.isArray(o.bm) ? o.bm.slice() : null;
            g.fatal = (o.ft && Array.isArray(o.ft.boom) && Array.isArray(o.ft.opened))
                ? { type: String(o.ft.type), boom: o.ft.boom.slice(), opened: o.ft.opened.slice() } : null;
            if (g.status === 'lost' && !g.boom) return null;
            return g;
        } catch (_) { return null; }
    };

    root.MinesCore = {
        LEVELS: LEVELS,
        LIMITS: LIMITS,
        HIDDEN: HIDDEN, OPEN: OPEN, FLAG: FLAG, QUESTION: QUESTION,
        maxMines: maxMines,
        clampBoard: clampBoard,
        levelOf: levelOf,
        topology: topology,
        numbers: numbers,
        countMines: countMines,
        threeBV: threeBV,
        place: place,
        packBits: packBits,
        unpackBits: unpackBits,
        encodeBoard: encodeBoard,
        decodeBoard: decodeBoard,
        Game: Game
    };
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
